import { css, slotRecipe } from "dx-styles"
import {
  useState,
  type ComponentType,
  type ReactElement,
  type ReactNode,
  type SVGProps,
} from "react"

import { useWindowSize } from "../hooks/useWindowSize"
import { tokens } from "../theme"
import type { ElementProps } from "../types"

/** Below this the sidebar starts collapsed. */
export const DESKTOP_WIDTH = 1280

const RAIL_WIDTH = "58px"

/**
 * How much of the `logo` the rail keeps: its leading square, one control wide.
 * A logo is a mark followed by a wordmark, and the mark is what a rail shows.
 */
const MARK_WIDTH = tokens.size.xs

const SURFACE_PADDING_BLOCK = "24px"

const HANDLE_ATTR = "data-sidebar-handle"

const HANDLE_INSET_BLOCK = `calc(${SURFACE_PADDING_BLOCK} + (${tokens.size.md} - ${tokens.size.xs}) / 2)`

const HANDLE_INSET_INLINE = "calc(50% + 1px)"

/**
 * The glass panel has no token — `tokens.gradient.weak` is the only gradient
 * the contract names, and this wash plus the layered ambient shadow are
 * specific to the sidebar. Kept as literals rather than widening the contract
 * for a single consumer. The wash already folds in the 70% opacity the design
 * applies to the gradient layer.
 */
const glassWash =
  "linear-gradient(96deg, rgba(255, 255, 255, 0.42) 2.72%, rgba(255, 255, 255, 0.56) 100.25%)"

const ambientShadow = [
  "89px 44px 28px 0 rgba(70, 40, 83, 0)",
  "57px 28px 25px 0 rgba(70, 40, 83, 0.01)",
  "32px 16px 21px 0 rgba(70, 40, 83, 0.03)",
  "14px 7px 16px 0 rgba(70, 40, 83, 0.04)",
  "4px 2px 9px 0 rgba(70, 40, 83, 0.05)",
].join(", ")

/**
 * Seven slots, one axis: `open` narrows the panel, and the header, the logo,
 * the nav and the rows inside it all have to move with it. A slot recipe keeps
 * that axis in one place — the alternative is the same variant written seven
 * times.
 *
 * Private, unlike `cardRecipe`/`buttonRecipe`: `open` is internal state, not a
 * prop, so there is nothing for a caller to select.
 */
const sidebarRecipe = slotRecipe({
  slots: ["surface", "header", "brand", "divider", "nav", "toggle", "chevron"],
  base: {
    surface: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      flexShrink: 0,
      position: "relative",
      zIndex: 1,
      gap: "16px",
      boxSizing: "border-box",
      width: "258px",
      height: "100dvh",
      paddingInline: "8px",
      paddingBlock: SURFACE_PADDING_BLOCK,
      color: tokens.color.text,
      background: glassWash,
      backdropFilter: "blur(10px)",
      borderInlineEnd: "2px solid rgba(255, 255, 255, 0.85)",
      boxShadow: ambientShadow,
      transition: `width ${tokens.duration.normal} ease-out`,
      [`&:hover [${HANDLE_ATTR}]`]: {
        opacity: 1,
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
      width: "100%",
      minHeight: tokens.size.md,
      paddingInline: "13px",
      transition: `padding ${tokens.duration.normal} ease-out`,
    },
    brand: {
      display: "flex",
      alignItems: "center",
      flex: 1,
      // Never narrower than the mark, which keeps the wordmark the only part
      // the collapse takes.
      minWidth: MARK_WIDTH,
      maxWidth: "100%",
      height: tokens.size.md,
      padding: "6.5px",
      borderRadius: tokens.radius.md,
      overflow: "hidden",
      transition: `padding ${tokens.duration.normal} ease-out, max-width ${tokens.duration.normal} ease-out`,
      "& svg": {
        display: "block",
        flexShrink: 0,
        width: "100px",
        maxWidth: "none",
        height: "27px",
      },
    },
    divider: {
      flexShrink: 0,
      width: "218px",
      maxWidth: "100%",
      height: "1px",
      backgroundColor: tokens.color.secondary,
    },
    nav: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
      width: "100%",
      gap: "8px",
      paddingInline: "8px",
      overflowY: "auto",
      overflowX: "hidden",
      transition: `padding ${tokens.duration.normal} ease-out`,
    },
    toggle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      insetBlockStart: HANDLE_INSET_BLOCK,
      insetInlineEnd: tokens.space.none,
      transform: `translateX(${HANDLE_INSET_INLINE})`,
      width: tokens.size.xs,
      height: tokens.size.xs,
      padding: tokens.space.none,
      color: tokens.color.text,
      backgroundColor: tokens.color.surface,
      border: tokens.border.default,
      borderRadius: tokens.radius.full,
      boxShadow: tokens.shadow.base,
      cursor: "pointer",
      opacity: 0,
      transition: `opacity ${tokens.duration.fast} ease-out, background-color ${tokens.duration.fast} ease-out`,
      "&:hover": {
        opacity: 1,
        backgroundColor: tokens.color.hover,
      },
      "&:focus-visible": {
        opacity: 1,
        outline: `2px solid ${tokens.color.primary}`,
        outlineOffset: "2px",
      },
      "@media (pointer: coarse)": {
        opacity: 1,
      },
    },
    chevron: {
      transition: `d ${tokens.duration.normal} ease-out`,
    },
  },
  variants: {
    open: {
      true: {
        // Points back at the panel it collapses.
        chevron: { d: 'path("M10.25 3.5L5.75 8L10.25 12.5")' },
      },
      false: {
        surface: {
          width: RAIL_WIDTH,
        },
        header: {
          paddingInline: "8px",
        },
        brand: {
          maxWidth: MARK_WIDTH,
          padding: tokens.space.none,
        },
        /**
         * The rows need every pixel between the surface's padding to stay a
         * full-width pill, and their labels go transparent rather than leaving
         * the layout: a label that vanished in one frame would free its space
         * and snap the icon sideways while the panel was still narrowing.
         */
        nav: {
          paddingInline: tokens.space.none,
          "& a": {
            color: "transparent",
          },
          // Matches what the active row sets on itself, so the fade does not
          // come down to which of the two rules the extractor emitted last.
          "& a[data-status=active]": {
            color: "transparent",
          },
        },
        // The mirror image, so the two states interpolate through a flat
        // vertical bar at the halfway point and the chevron reads as turning
        // around rather than swapping.
        chevron: { d: 'path("M5.75 3.5L10.25 8L5.75 12.5")' },
      },
    },
  },
  defaultVariants: {
    open: true,
  },
})

const footer = css({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  marginBlockStart: "auto",
})

export const sidebarItem = css({
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  gap: "8px",
  height: tokens.size.md,
  padding: "12px",
  borderRadius: tokens.radius.md,
  color: tokens.color.text,
  fontFamily: tokens.font.body,
  fontSize: tokens.fontSize.h3,
  fontWeight: tokens.fontWeight.regular,
  lineHeight: tokens.lineHeight.normal,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textDecoration: "none",
  transition: `background-color ${tokens.duration.normal} ease-out, color ${tokens.duration.normal} ease-out`,
  "& svg": {
    flexShrink: 0,
    width: "16px",
    height: "16px",
    color: tokens.color.text,
  },
  "&[data-status=active] svg": {
    color: tokens.color.accentDark,
  },
  "&:not([data-status=active]):hover": {
    backgroundColor: tokens.color.hover,
  },
  "&[data-status=active]": {
    backgroundColor: tokens.color.accentSubtle,
    color: tokens.color.accentDark,
    fontWeight: tokens.fontWeight.semibold,
  },
  "&::after": {
    content: '""',
    marginInlineStart: "auto",
    flexShrink: 0,
    width: "6px",
    height: "6px",
    borderRadius: tokens.radius.full,
  },
  "&[data-status=active]::after": {
    backgroundColor: tokens.color.accent,
  },
})

type ToggleIconProps = {
  chevron: string
}

/**
 * The chevron the edge handle carries, pointing at whichever side the click
 * moves the panel towards.
 *
 * Both states are written with the same commands in the same order, because
 * the CSS `d` property only interpolates between paths that match that way —
 * anything else snaps. The attribute here is the open state and only stands in
 * until the slot class takes over.
 */
const ToggleIcon = ({ chevron }: ToggleIconProps): ReactElement => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path
        className={chevron}
        d="M10.25 3.5L5.75 8L10.25 12.5"
      />
    </svg>
  )
}

export type CollapsibleSidebarProps = ElementProps<"div"> & {
  /**
   * Names the landmark. A page may hold more than one `<nav>`, and an unlabelled
   * one is indistinguishable from the others in a screen reader's landmark list.
   */
  label?: string
  logo?: ReactNode
}

/**
 * The app-shell navigation panel.
 *
 * Open state is owned here rather than exposed as a variant, because it is
 * driven by the viewport: desktop starts open and mobile starts closed, and the
 * two breakpoints remember their own last choice — collapsing on a narrow window
 * should not also collapse it once the window is widened again.
 */
export const CollapsibleSidebar = ({
  label = "Main",
  logo,
  children,
  ...rest
}: CollapsibleSidebarProps): ReactElement => {
  const { width: windowWidth } = useWindowSize()
  const isDesktop = windowWidth >= DESKTOP_WIDTH
  const [isDesktopOpen, setIsDesktopOpen] = useState(true)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const isOpen = isDesktop ? isDesktopOpen : isMobileOpen
  const slots = sidebarRecipe({ open: isOpen })

  return (
    <div
      {...rest}
      className={slots.surface}
    >
      <div className={slots.header}>
        {logo ? <div className={slots.brand}>{logo}</div> : null}
      </div>

      <button
        type="button"
        className={slots.toggle}
        aria-expanded={isOpen}
        aria-label={
          isOpen ? `Collapse ${label} navigation` : `Expand ${label} navigation`
        }
        onClick={() => {
          if (isDesktop) {
            setIsDesktopOpen(!isDesktopOpen)
            return
          }

          setIsMobileOpen(!isMobileOpen)
        }}
        {...{ [HANDLE_ATTR]: "" }}
      >
        <ToggleIcon chevron={slots.chevron} />
      </button>

      <div className={slots.divider} />

      <nav
        aria-label={label}
        className={slots.nav}
        data-testid="sidebar-content"
      >
        {children}
      </nav>
    </div>
  )
}

export type CollapsibleSidebarItemProps = ElementProps<"a"> & {
  icon?: ComponentType<SVGProps<SVGSVGElement>>
}

/**
 * A nav link. A plain anchor rather than router-aware: an app with a router
 * renders its own `Link` with the exported `sidebarItem` class instead, and the
 * active styling keys off the `data-status="active"` such a link sets.
 */
const CollapsibleSidebarItem = ({
  icon: Icon,
  children,
  ...rest
}: CollapsibleSidebarItemProps): ReactElement => {
  return (
    <a
      {...rest}
      className={sidebarItem}
    >
      {Icon ? <Icon aria-hidden="true" /> : null}
      {children}
    </a>
  )
}

const CollapsibleSidebarFooter = (props: ElementProps<"div">): ReactElement => {
  return (
    <div
      {...props}
      className={footer}
    />
  )
}

CollapsibleSidebar.Item = CollapsibleSidebarItem
CollapsibleSidebar.Footer = CollapsibleSidebarFooter
